import { useEffect, useState } from 'react'
import { fetchVehicleDefectProfile } from './client'
import type { VehicleDefectProfile } from './types'

/**
 * Loads the model's defect profile for one plate, once `enabled` turns true.
 *
 * The same shape as `useVehicleRarity`, and for a stronger version of the same reason. A cache miss
 * costs the API a dozen RDW requests and several seconds — the defect dataset carries no model
 * column, so the cohort has to be listed and then counted a batch of plates at a time — which is
 * exactly the wait the report must never sit behind. The caller passes `enabled: true` once the
 * report is on screen.
 *
 * **A failure is silent.** No error state, nothing rendered: the report the reader came for has
 * already arrived, and a red panel about a model's inspection statistics would be a worse page than
 * one without them.
 */
export function useVehicleDefectProfile(
  plate: string,
  enabled: boolean,
): VehicleDefectProfile | undefined {
  const [loaded, setLoaded] = useState<{ plate: string; profile: VehicleDefectProfile }>()

  useEffect(() => {
    if (!enabled) {
      return
    }

    const controller = new AbortController()

    fetchVehicleDefectProfile(plate, controller.signal)
      .then((profile) => setLoaded({ plate, profile }))
      .catch(() => {
        // Nothing to do and nothing to say — see above.
      })

    return () => controller.abort()
  }, [plate, enabled])

  // Keyed on the plate it was fetched for rather than cleared on the way out: clearing happens in an
  // effect, and the render before it would hand the new car the previous car's figures. Percentages
  // about the wrong model under the right plate is the one failure this section could produce.
  return loaded?.plate === plate ? loaded.profile : undefined
}
